import { useCallback, useMemo, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import { CSVLink } from 'react-csv'
import { ExclamationIcon, XIcon } from '@heroicons/react/outline'

import { Modal } from 'components/Modal'
import { Close } from 'components/Buttons/Close'
import { Button } from 'components/Buttons/Basic'
import { ClientChip } from 'models/clientChips'
import { OrderConnectivityStatus } from 'models'
import { ReadResult } from 'interfaces/queryOptions'
import { FineSimulationResult } from 'models/chipCancellation'
import { simulateCancellation } from 'modules/Clients/services/chipCancellation'
import { listClientItems } from 'modules/Clients/services/read'
import { updateItemStatus } from 'modules/Orders/services/update'
import { DatePickerWithError } from 'components/FormInputs/DatePicker'
import { InputWithValidationError } from 'components/FormInputs/Input'
import { ResizibleTextArea } from 'components/FormInputs/TextArea/ResizibleTextArea'
import { SimpleButton } from 'components/FormInputs/Button/SimpleButton'
import { amount, currency, date } from 'utils'
import { groupChipsByOrder } from '../groupChipsByOrder'

// Chip precisa estar nesse item_status pra fazer sentido entrar numa simulação/
// cancelamento — mesma regra usada em Chips/index.tsx (isEligibleForStatusChange)
// pro checkbox da listagem.
const CANCELABLE_ITEM_STATUSES = ['ordered', 'suspended']

interface Props {
  isOpen: boolean
  setOpen: (isOpen: boolean) => void
  clientId: number
  onCanceled: () => void
}

// 'build': monta a lista digitando seriais (cada um entra "não validado" e
// resolve em background) — mesma ideia da inserção de itens em pedidos.
// 'confirm': depois de clicar "Simular", mostra a multa calculada e confirma.
type Step = 'build' | 'confirm'

type ChipEntryStatus = 'validating' | 'valid' | 'invalid'

interface ChipEntry {
  serial: string
  status: ChipEntryStatus
  chip?: ReadResult<ClientChip>
  errorMessage?: string
}

export function CancelChipsModal({
  isOpen,
  setOpen,
  clientId,
  onCanceled
}: Props) {
  const [step, setStep] = useState<Step>('build')
  const [referenceDate, setReferenceDate] = useState<Date>(new Date())
  const [simulation, setSimulation] = useState<FineSimulationResult>()
  const [isSimulating, setIsSimulating] = useState(false)
  const [isConfirming, setIsConfirming] = useState(false)

  const [entries, setEntries] = useState<ChipEntry[]>([])
  const [serialInput, setSerialInput] = useState('')

  const [isBulkModalOpen, setIsBulkModalOpen] = useState(false)
  const [bulkInput, setBulkInput] = useState('')
  const csvInputRef = useRef<HTMLInputElement>(null)

  const updateEntry = useCallback(
    (serial: string, patch: Partial<ChipEntry>) => {
      setEntries((prev) =>
        prev.map((entry) =>
          entry.serial === serial ? { ...entry, ...patch } : entry
        )
      )
    },
    []
  )

  const onClose = useCallback(() => {
    setOpen(false)
    setStep('build')
    setSimulation(undefined)
    setReferenceDate(new Date())
    setEntries([])
    setSerialInput('')
    setBulkInput('')
    setIsBulkModalOpen(false)
  }, [setOpen])

  // Resolve um serial já colocado na lista (como "validando") em background —
  // usado tanto pela adição unitária quanto pela em lote.
  const validateSerial = useCallback(
    async (serial: string) => {
      try {
        const result = await listClientItems<ClientChip>('chips', clientId, {
          filters: [{ key: 'serial', op: 'eq', value: serial }],
          limit: 1
        })

        const chip = result.data[0]

        if (chip == null) {
          updateEntry(serial, {
            status: 'invalid',
            errorMessage: 'Chip não encontrado'
          })
          return
        }

        if (
          !CANCELABLE_ITEM_STATUSES.includes(chip.attributes.item_status ?? '')
        ) {
          updateEntry(serial, {
            status: 'invalid',
            errorMessage: 'Não pode ser cancelado no estado atual'
          })
          return
        }

        updateEntry(serial, { status: 'valid', chip })
      } catch (error: any) {
        updateEntry(serial, {
          status: 'invalid',
          errorMessage: error.suggestedMessage ?? 'Erro ao buscar chip'
        })
      }
    },
    [clientId, updateEntry]
  )

  // Mesma resolução acima, mas pra um lote inteiro numa request só (filtro
  // "in" com todos os seriais) em vez de uma request por serial.
  const validateSerialsBatch = useCallback(
    async (serials: string[]) => {
      try {
        const result = await listClientItems<ClientChip>('chips', clientId, {
          filters: [{ key: 'serial', op: 'in', value: serials }],
          limit: serials.length
        })

        const chipBySerial = new Map(
          result.data.map((chip) => [chip.attributes.serial, chip])
        )

        for (const serial of serials) {
          const chip = chipBySerial.get(serial)

          if (chip == null) {
            updateEntry(serial, {
              status: 'invalid',
              errorMessage: 'Chip não encontrado'
            })
            continue
          }

          if (
            !CANCELABLE_ITEM_STATUSES.includes(
              chip.attributes.item_status ?? ''
            )
          ) {
            updateEntry(serial, {
              status: 'invalid',
              errorMessage: 'Não pode ser cancelado no estado atual'
            })
            continue
          }

          updateEntry(serial, { status: 'valid', chip })
        }
      } catch (error: any) {
        for (const serial of serials) {
          updateEntry(serial, {
            status: 'invalid',
            errorMessage: error.suggestedMessage ?? 'Erro ao buscar chip'
          })
        }
      }
    },
    [clientId, updateEntry]
  )

  // Adiciona o serial na lista imediatamente como "validando" e resolve em
  // background — não bloqueia o campo pra digitar o próximo serial na hora.
  const handleAddSerial = useCallback(() => {
    const serial = serialInput.trim()
    if (!serial) return

    if (entries.some((entry) => entry.serial === serial)) {
      toast.error('Esse serial já está na lista')
      return
    }

    setEntries((prev) => [...prev, { serial, status: 'validating' }])
    setSerialInput('')
    validateSerial(serial)
  }, [serialInput, entries, validateSerial])

  const handleBulkAdd = useCallback(() => {
    const codes = bulkInput
      .split(/[\n,;\s]+/)
      .map((s) => s.trim())
      .filter(Boolean)

    const existingSerials = new Set(entries.map((entry) => entry.serial))
    const newSerials: string[] = []
    let skipped = 0

    for (const serial of codes) {
      if (existingSerials.has(serial)) {
        skipped++
        continue
      }
      existingSerials.add(serial)
      newSerials.push(serial)
    }

    if (skipped > 0) {
      toast.warn(`${skipped} serial(is) ignorado(s) por já estarem na lista.`)
    }

    if (newSerials.length === 0) {
      setBulkInput('')
      setIsBulkModalOpen(false)
      return
    }

    setEntries((prev) => [
      ...prev,
      ...newSerials.map((serial) => ({
        serial,
        status: 'validating' as const
      }))
    ])

    validateSerialsBatch(newSerials)

    setBulkInput('')
    setIsBulkModalOpen(false)
  }, [bulkInput, entries, validateSerialsBatch])

  const onCsvImport = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    const reader = new FileReader()
    reader.onload = (ev) => {
      const text = ev.target?.result as string
      const codes = text
        .split(/[\n,;\r]+/)
        .map((s) => s.trim())
        .filter(Boolean)
        .join('\n')
      setBulkInput((prev) => (prev ? `${prev}\n${codes}` : codes))
    }
    reader.readAsText(file)
    e.target.value = ''
  }, [])

  const handleRemoveEntry = useCallback((serial: string) => {
    setEntries((prev) => prev.filter((entry) => entry.serial !== serial))
  }, [])

  const validChips = useMemo(
    () =>
      entries
        .filter((entry) => entry.status === 'valid')
        .map((entry) => entry.chip as ReadResult<ClientChip>),
    [entries]
  )

  const isValidatingAny = entries.some((entry) => entry.status === 'validating')

  const handleSimulate = useCallback(async () => {
    if (isValidatingAny) {
      toast.error('Aguarde a validação de todos os chips')
      return
    }

    if (validChips.length === 0) {
      toast.error('Adicione ao menos um chip válido antes de simular')
      return
    }

    setIsSimulating(true)
    try {
      const itemIds = validChips.map((chip) => chip.id)
      const result = await simulateCancellation(
        clientId,
        itemIds,
        referenceDate
      )
      setSimulation(result)
      setStep('confirm')
    } catch (error: any) {
      toast.error(error.suggestedMessage ?? 'Erro ao simular cancelamento')
    } finally {
      setIsSimulating(false)
    }
  }, [isValidatingAny, validChips, clientId, referenceDate])

  const validItemIds = new Set(validChips.map((chip) => chip.id))
  const simulationItems = (simulation?.items ?? []).filter((item) =>
    validItemIds.has(item.item_id)
  )
  // Chips selecionados podem ter multas simuladas em moedas diferentes — soma
  // por moeda em vez de um total único que ignoraria a moeda dos demais itens.
  const totalsByCurrency = simulationItems.reduce<Record<string, number>>(
    (totals, item) => {
      totals[item.currency] = (totals[item.currency] ?? 0) + item.fine_amount
      return totals
    },
    {}
  )
  const totalLabel =
    Object.keys(totalsByCurrency).length === 0
      ? 'sem multa'
      : Object.entries(totalsByCurrency)
          .map(
            ([curr, total]) =>
              `${currency(curr.toLowerCase())} ${amount(total)}`
          )
          .join(' + ')

  const csvData = [
    [`Simulação de cancelamento de chips em ${date(referenceDate)}`],
    [],
    ['Serial', 'Valor'],
    ...validChips.map((chip) => {
      const item = simulationItems.find((i) => i.item_id === chip.id)
      const value = item
        ? `${currency(item.currency.toLowerCase())} ${amount(item.fine_amount)}`
        : 'Sem multa'

      return [chip.attributes.serial ?? '-', value]
    })
  ]

  const onConfirm = useCallback(async () => {
    setIsConfirming(true)

    const { groups, skipped } = groupChipsByOrder(validChips)

    if (skipped.length > 0) {
      toast.error(
        `${skipped.length} chip(s) sem pedido associado foram ignorados e não tiveram o cancelamento processado.`
      )
    }

    const outcomes = await Promise.all(
      Object.entries(groups).map(async ([orderId, chipsInOrder]) => {
        try {
          // orderId é o hashid vindo de chip.relationships.order.id (chave do
          // agrupamento), não um número — updateItemStatus só embute na URL.
          await updateItemStatus(
            orderId,
            OrderConnectivityStatus.canceled,
            chipsInOrder.map((chip) => ({
              attributes: { item_id: chip.id, created_at: referenceDate }
            }))
          )
          return { orderId, success: true }
        } catch (error: any) {
          toast.error(
            error.suggestedMessage ??
              `Erro ao cancelar chips do pedido ${orderId}`
          )
          return { orderId, success: false }
        }
      })
    )

    const failedOrderIds = outcomes
      .filter((outcome) => !outcome.success)
      .map((outcome) => outcome.orderId)

    setIsConfirming(false)

    if (failedOrderIds.length === 0 && skipped.length === 0) {
      toast.success('Chip(s) cancelado(s) com sucesso!')
      onCanceled()
      onClose()
      return
    }

    // Fecha o modal mesmo em falha parcial: o pai já limpou/vai limpar a
    // seleção, então deixar o passo "confirm" aberto e habilitado permite um
    // segundo clique com 0 chips selecionados, que dispararia um toast de
    // sucesso vazio.
    if (failedOrderIds.length < Object.keys(groups).length) {
      toast.success('Parte dos chips selecionados foi cancelada com sucesso.')
      onCanceled()
      onClose()
    }
  }, [validChips, referenceDate, onCanceled, onClose])

  return (
    <Modal
      open={isOpen}
      title="Cancelar chips"
      onClose={onClose}
      width="sm:max-w-2xl"
    >
      <div className="animate-fade-in-down">
        <div className="col-span-12">
          <DatePickerWithError
            label="Data do cancelamento"
            name="reference_date"
            date={referenceDate}
            onChange={(value: Date | null) => value && setReferenceDate(value)}
            maxDate={new Date()}
            disabled={step === 'confirm'}
          />
        </div>

        {step === 'build' && (
          <div className="mt-4 flex items-end gap-x-2">
            <div className="flex-1">
              <InputWithValidationError
                label="Adicionar chip por serial"
                name="chip_serial"
                value={serialInput}
                onChange={setSerialInput}
                placeholder="Digite o serial e pressione Enter"
                onKeyDown={(e: KeyboardEvent) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    handleAddSerial()
                  }
                }}
              />
            </div>
            <SimpleButton
              className="px-4 py-2.5 rounded-md border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              onClick={handleAddSerial}
              disabled={!serialInput.trim()}
            >
              Adicionar
            </SimpleButton>
            <SimpleButton
              className="px-4 py-2.5 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-emerald-600 hover:bg-emerald-700 focus:outline-none whitespace-nowrap"
              onClick={() => setIsBulkModalOpen(true)}
            >
              Inserção em lote
            </SimpleButton>
          </div>
        )}

        {step === 'build' ? (
          entries.length === 0 ? (
            <p className="mt-4 text-sm text-gray-500">
              Nenhum chip adicionado ainda. Busque por serial acima.
            </p>
          ) : (
            <div className="mt-4">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-light text-gray-500 uppercase">
                      Serial
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-light text-gray-500 uppercase">
                      Status
                    </th>
                    <th className="px-3 py-2" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {entries.map((entry) => (
                    <tr key={entry.serial}>
                      <td className="px-3 py-2 text-sm">{entry.serial}</td>
                      <td className="px-3 py-2 text-sm">
                        {entry.status === 'validating' && (
                          <span className="text-gray-500">Validando...</span>
                        )}
                        {entry.status === 'valid' && (
                          <span className="text-emerald-600">Pronto</span>
                        )}
                        {entry.status === 'invalid' && (
                          <span className="text-red-600">
                            {entry.errorMessage ?? 'Inválido'}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-sm">
                        <button
                          type="button"
                          aria-label={`Remover chip ${entry.serial}`}
                          onClick={() => handleRemoveEntry(entry.serial)}
                          className="text-gray-400 hover:text-gray-600"
                        >
                          <XIcon className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        ) : (
          <div className="mt-4">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-light text-gray-500 uppercase">
                    Serial
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-light text-gray-500 uppercase">
                    Multa prevista
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {validChips.map((chip) => {
                  const item = simulationItems.find(
                    (i) => i.item_id === chip.id
                  )
                  return (
                    <tr key={chip.id}>
                      <td className="px-3 py-2 text-sm">
                        {chip.attributes.serial ?? '-'}
                      </td>
                      <td className="px-3 py-2 text-sm">
                        {item
                          ? `${currency(item.currency.toLowerCase())} ${amount(
                              item.fine_amount
                            )}`
                          : 'Sem multa'}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>

            <div className="mt-4 rounded-md bg-yellow-50 p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <ExclamationIcon
                    className="h-5 w-5 text-yellow-400"
                    aria-hidden="true"
                  />
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-yellow-800">
                    Confirmar cancelamento
                  </h3>
                  <div className="mt-2 text-sm text-yellow-700">
                    <p>
                      Tem certeza que deseja cancelar {validChips.length}{' '}
                      chip(s)? Esta ação não pode ser desfeita. Multa total
                      prevista: {totalLabel}.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="mt-8 flex space-x-4 justify-end">
          <Close onClick={onClose} />

          {step === 'confirm' && simulation && (
            <button
              type="button"
              className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none"
            >
              <CSVLink
                data={csvData}
                filename={`simulacao-cancelamento-chips-${date(
                  referenceDate
                ).replace(/\//g, '-')}.csv`}
              >
                Exportar
              </CSVLink>
            </button>
          )}

          {step === 'build' ? (
            <Button
              type="button"
              label="Simular"
              disabled={
                isSimulating || isValidatingAny || validChips.length === 0
              }
              loading={isSimulating}
              onClick={handleSimulate}
            />
          ) : (
            <Button
              type="button"
              label="Confirmar cancelamento"
              disabled={isConfirming}
              loading={isConfirming}
              onClick={onConfirm}
            />
          )}
        </div>
      </div>

      {/* Modal de inserção em lote */}
      <Modal
        open={isBulkModalOpen}
        title="Inserção em lote de chips"
        width="sm:max-w-xl"
        onClose={() => {
          setIsBulkModalOpen(false)
          setBulkInput('')
        }}
      >
        <div className="space-y-4 mt-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-500">
              Cole os seriais abaixo ou importe um CSV.
            </p>
            <button
              type="button"
              className="text-sm text-blue-600 hover:text-blue-800 font-medium mr-2"
              onClick={() => csvInputRef.current?.click()}
            >
              Importar CSV
            </button>
            <input
              ref={csvInputRef}
              type="file"
              accept=".csv,text/csv,text/plain"
              className="hidden"
              onChange={onCsvImport}
            />
          </div>
          <ResizibleTextArea
            label=""
            name="bulk_serials"
            value={bulkInput}
            onChange={setBulkInput}
            placeholder={'00000000000000000001\n00000000000000000002'}
          />
          {bulkInput.trim() && (
            <p className="text-s text-gray-500 text-right">
              {bulkInput.split(/[\n,;\s]+/).filter(Boolean).length} seriais
              detectados
            </p>
          )}
          <div className="flex justify-end gap-x-3 mt-4">
            <SimpleButton
              className="px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none"
              onClick={() => {
                setIsBulkModalOpen(false)
                setBulkInput('')
              }}
            >
              Cancelar
            </SimpleButton>
            <SimpleButton onClick={handleBulkAdd} disabled={!bulkInput.trim()}>
              Adicionar à lista
            </SimpleButton>
          </div>
        </div>
      </Modal>
    </Modal>
  )
}
